/**
 * StorageCleanup — Firestore storage management page.
 * Owner-only. Lets you preview and delete old documents from:
 *   • analyticsEvents  — page-view/scroll tracking events
 *   • ticketChecks     — KDS ticket state documents
 */

import React, { useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  collection, query, orderBy, limit, getDocs,
  deleteDoc, doc, where, Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import './StorageCleanup.css';

const ALLOWED_ROLES = ['owner'];

// ── helpers ───────────────────────────────────────────────────────────────────
function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function daysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ icon, title, subtitle, children }) {
  return (
    <div className="sc__section">
      <div className="sc__section-head">
        <span className="sc__section-icon">{icon}</span>
        <div>
          <div className="sc__section-title">{title}</div>
          <div className="sc__section-sub">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Analytics Events cleanup ──────────────────────────────────────────────────
function AnalyticsCleanup() {
  const [olderThan, setOlderThan] = useState('30');
  const [preview, setPreview]   = useState(null); // { count, oldest, newest }
  const [previewing, setPreviewing] = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [deleted,   setDeleted]   = useState(null);
  const [error,     setError]     = useState(null);

  const doPreview = useCallback(async () => {
    setPreviewing(true); setError(null); setPreview(null); setDeleted(null);
    try {
      const cutoff = daysAgo(parseInt(olderThan, 10));
      const q = query(
        collection(db, 'analyticsEvents'),
        where('timestamp', '<', cutoff),
        orderBy('timestamp', 'asc'),
        limit(1000),
      );
      const snap = await getDocs(q);
      let oldest = null, newest = null;
      snap.docs.forEach(d => {
        const ts = d.data().timestamp;
        if (!oldest || ts < oldest) oldest = ts;
        if (!newest || ts > newest) newest = ts;
      });
      setPreview({ count: snap.size, oldest, newest, docs: snap.docs });
    } catch (e) {
      setError(e.message);
    } finally { setPreviewing(false); }
  }, [olderThan]);

  const doDelete = useCallback(async () => {
    if (!preview?.docs?.length) return;
    setDeleting(true); setError(null);
    try {
      // Delete in batches of 100 to avoid overloading
      const all = preview.docs;
      let count = 0;
      for (let i = 0; i < all.length; i += 100) {
        await Promise.all(all.slice(i, i + 100).map(d => deleteDoc(doc(db, 'analyticsEvents', d.id))));
        count += Math.min(100, all.length - i);
      }
      setDeleted(count);
      setPreview(null);
    } catch (e) {
      setError(e.message);
    } finally { setDeleting(false); }
  }, [preview]);

  return (
    <Section icon="📊" title="Analytics Events" subtitle="Page-view and scroll tracking events written by usePageTracking.js">
      <div className="sc__controls">
        <label className="sc__label">Delete events older than</label>
        <select className="sc__select" value={olderThan} onChange={e => { setOlderThan(e.target.value); setPreview(null); setDeleted(null); }}>
          <option value="7">7 days</option>
          <option value="14">14 days</option>
          <option value="30">30 days</option>
          <option value="60">60 days</option>
          <option value="90">90 days</option>
          <option value="180">6 months</option>
          <option value="365">1 year</option>
        </select>
        <button className="sc__btn sc__btn--preview" onClick={doPreview} disabled={previewing || deleting}>
          {previewing ? '⏳ Scanning…' : '🔍 Preview'}
        </button>
      </div>

      {error && <div className="sc__error">⚠️ {error}</div>}

      {deleted != null && (
        <div className="sc__success">✅ Deleted {deleted.toLocaleString()} analytics event{deleted !== 1 ? 's' : ''}.</div>
      )}

      {preview && (
        <div className="sc__preview">
          <div className="sc__preview-row">
            <span className="sc__preview-stat"><span className="sc__preview-num">{preview.count.toLocaleString()}</span> documents found</span>
            {preview.oldest && <span className="sc__preview-range">Oldest: {fmtDate(preview.oldest)}</span>}
            {preview.newest && <span className="sc__preview-range">Newest: {fmtDate(preview.newest)}</span>}
          </div>
          {preview.count === 0 ? (
            <div className="sc__empty">No documents to delete.</div>
          ) : (
            <button className="sc__btn sc__btn--delete" onClick={doDelete} disabled={deleting}>
              {deleting ? '⏳ Deleting…' : `🗑️ Delete ${preview.count.toLocaleString()} document${preview.count !== 1 ? 's' : ''}`}
            </button>
          )}
          {preview.count === 1000 && (
            <div className="sc__warn">⚠️ Preview capped at 1,000 — run again after deleting to clear more.</div>
          )}
        </div>
      )}
    </Section>
  );
}

// ── Ticket Checks cleanup ─────────────────────────────────────────────────────
function TicketChecksCleanup() {
  const [olderThan, setOlderThan] = useState('30');
  const [onlyCompleted, setOnlyCompleted] = useState(true);
  const [preview, setPreview]   = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [deleted,   setDeleted]   = useState(null);
  const [error,     setError]     = useState(null);

  const doPreview = useCallback(async () => {
    setPreviewing(true); setError(null); setPreview(null); setDeleted(null);
    try {
      const cutoff = daysAgo(parseInt(olderThan, 10));
      // Single-field query only — no composite index needed.
      // Filter manuallyCompleted client-side to avoid requiring a composite index.
      const q = query(
        collection(db, 'ticketChecks'),
        where('updatedAt', '<', cutoff),
        orderBy('updatedAt', 'asc'),
        limit(500),
      );
      const snap = await getDocs(q);
      const matchingDocs = onlyCompleted
        ? snap.docs.filter(d => !!d.data().manuallyCompleted)
        : snap.docs;
      let oldest = null, newest = null;
      const sample = [];
      matchingDocs.forEach(d => {
        const data = d.data();
        const ts = data.updatedAt;
        if (!oldest || ts < oldest) oldest = ts;
        if (!newest || ts > newest) newest = ts;
        if (sample.length < 5) sample.push({ id: d.id, ticketName: data.ticketName || '—', updatedAt: ts, completed: !!data.manuallyCompleted });
      });
      setPreview({ count: matchingDocs.length, oldest, newest, docs: matchingDocs, sample });
    } catch (e) {
      setError(e.message);
    } finally { setPreviewing(false); }
  }, [olderThan, onlyCompleted]);

  const doDelete = useCallback(async () => {
    if (!preview?.docs?.length) return;
    setDeleting(true); setError(null);
    try {
      const all = preview.docs;
      let count = 0;
      for (let i = 0; i < all.length; i += 100) {
        await Promise.all(all.slice(i, i + 100).map(d => deleteDoc(doc(db, 'ticketChecks', d.id))));
        count += Math.min(100, all.length - i);
      }
      setDeleted(count);
      setPreview(null);
    } catch (e) {
      setError(e.message);
    } finally { setDeleting(false); }
  }, [preview]);

  return (
    <Section icon="🎫" title="Ticket Checks" subtitle="KDS ticket state documents stored under ticketChecks/{orderId}">
      <div className="sc__controls">
        <label className="sc__label">Delete records older than</label>
        <select className="sc__select" value={olderThan} onChange={e => { setOlderThan(e.target.value); setPreview(null); setDeleted(null); }}>
          <option value="7">7 days</option>
          <option value="14">14 days</option>
          <option value="30">30 days</option>
          <option value="60">60 days</option>
          <option value="90">90 days</option>
          <option value="180">6 months</option>
        </select>
        <label className="sc__check-label">
          <input type="checkbox" checked={onlyCompleted} onChange={e => { setOnlyCompleted(e.target.checked); setPreview(null); setDeleted(null); }} />
          Only completed tickets
        </label>
        <button className="sc__btn sc__btn--preview" onClick={doPreview} disabled={previewing || deleting}>
          {previewing ? '⏳ Scanning…' : '🔍 Preview'}
        </button>
      </div>

      {error && <div className="sc__error">⚠️ {error}</div>}

      {deleted != null && (
        <div className="sc__success">✅ Deleted {deleted.toLocaleString()} ticket record{deleted !== 1 ? 's' : ''}.</div>
      )}

      {preview && (
        <div className="sc__preview">
          <div className="sc__preview-row">
            <span className="sc__preview-stat"><span className="sc__preview-num">{preview.count.toLocaleString()}</span> documents found</span>
            {preview.oldest && <span className="sc__preview-range">Oldest: {fmtDate(preview.oldest)}</span>}
            {preview.newest && <span className="sc__preview-range">Newest: {fmtDate(preview.newest)}</span>}
          </div>

          {preview.sample?.length > 0 && (
            <div className="sc__sample">
              <div className="sc__sample-title">Sample (first {preview.sample.length})</div>
              <table className="sc__sample-tbl">
                <thead><tr><th>Order ID</th><th>Ticket Name</th><th>Updated</th><th>Status</th></tr></thead>
                <tbody>
                  {preview.sample.map(r => (
                    <tr key={r.id}>
                      <td className="sc__mono">{r.id.slice(-8).toUpperCase()}</td>
                      <td>{r.ticketName}</td>
                      <td>{fmtDate(r.updatedAt)}</td>
                      <td><span className={`sc__status sc__status--${r.completed ? 'done' : 'open'}`}>{r.completed ? '✅ Done' : '🟡 Open'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.count === 0 ? (
            <div className="sc__empty">No documents to delete.</div>
          ) : (
            <button className="sc__btn sc__btn--delete" onClick={doDelete} disabled={deleting}>
              {deleting ? '⏳ Deleting…' : `🗑️ Delete ${preview.count.toLocaleString()} record${preview.count !== 1 ? 's' : ''}`}
            </button>
          )}
          {preview.count === 500 && (
            <div className="sc__warn">⚠️ Preview capped at 500 — run again after deleting to clear more.</div>
          )}
        </div>
      )}
    </Section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function StorageCleanup() {
  const { role } = useAuth();
  if (!ALLOWED_ROLES.includes(role)) return <Navigate to="/" replace />;

  return (
    <div className="sc__page">
      <div className="sc__header">
        <div className="sc__header-icon">🗄️</div>
        <div>
          <h1 className="sc__title">Storage Cleanup</h1>
          <p className="sc__subtitle">Preview and delete old Firestore documents to keep your database lean and fast.</p>
        </div>
      </div>

      <div className="sc__warning-banner">
        ⚠️ <strong>Irreversible.</strong> Deleted documents cannot be recovered. Always preview before deleting.
      </div>

      <div className="sc__sections">
        <AnalyticsCleanup />
        <TicketChecksCleanup />
      </div>
    </div>
  );
}
